import { useCallback } from 'react';
import { create } from 'zustand';

/**
 * Global confirm/alert modal store.
 *
 * Usage:
 *   import { useConfirm } from '../stores/confirmStore';
 *
 *   const confirm = useConfirm();
 *   const ok = await confirm({ title: 'Delete?', message: 'This cannot be undone.' });
 *   if (ok) { ... }
 *
 *   // Alert (info only, single OK button):
 *   await confirm({ title: 'Done', message: 'Upload complete.', mode: 'alert' });
 *
 *   // Custom buttons:
 *   await confirm({
 *     title: 'Unsaved changes',
 *     message: 'You have unsaved edits.',
 *     confirmLabel: 'Discard',
 *     cancelLabel: 'Keep editing',
 *     variant: 'danger',
 *   });
 *
 *   // Non-destructive confirm (blue, not red). `variant: 'primary'` or `danger: false`:
 *   await confirm({ title: 'Send this invoice?', confirmLabel: 'Send', variant: 'primary' });
 *
 *   // Focus the safe button when the dialog opens (default is the confirm button):
 *   await confirm({ title: 'Void?', confirmLabel: 'Void', initialFocus: 'cancel' });
 *
 *   // Three-way choice (additive; useConfirm stays boolean). Resolves
 *   // 'save' | 'discard' | 'keep'; Escape / backdrop / the cancel button are 'keep':
 *   const choose = useChoice();
 *   const answer = await choose({
 *     title: 'Unsaved changes',
 *     message: 'Save before leaving?',
 *     saveLabel: 'Save',
 *     discardLabel: 'Discard',
 *     keepLabel: 'Keep editing',
 *   });
 */

// What a dismiss (Escape, backdrop, Cancel button, or being replaced by another
// dialog) resolves to, per mode. Kept here so the store and ConfirmModal agree.
const DISMISS_RESULT = { confirm: false, alert: false, prompt: null, choice: 'keep' };

// A lookup, not `DISMISS_RESULT[mode] ?? false`: a prompt's dismiss value IS null,
// and `??` would turn it into false.
export function dismissResultFor(mode) {
  return mode in DISMISS_RESULT ? DISMISS_RESULT[mode] : false;
}

// Which variant a dialog gets when the caller did not name one. `variant` wins;
// `danger: false` is the non-destructive spelling (callers already pass
// `danger: true` for destructive confirms); anything else keeps the historical
// default, red, so existing call sites render unchanged.
function resolveVariant(options) {
  if (options.variant) return options.variant;
  return options.danger === false ? 'primary' : 'danger';
}

const useConfirmStore = create((set, get) => ({
  isOpen: false,
  title: '',
  message: '',
  confirmLabel: 'Confirm',
  cancelLabel: 'Cancel',
  variant: 'danger', // 'danger' | 'primary' | 'warning'
  mode: 'confirm', // 'confirm' | 'alert' | 'prompt' | 'choice'
  // Which button takes focus on open: 'confirm' (default) | 'cancel'. Prompt mode
  // always focuses its input, and a dialog with no cancel button focuses confirm.
  initialFocus: 'confirm',
  // Choice mode only: the label of the third (discard) action, and how it is drawn:
  // 'danger' (red, a throw-away) or 'neutral' (an action that keeps the work, e.g. "Keep as a
  // recovery draft on this device": red there would read as a loss).
  discardLabel: 'Discard',
  discardTone: 'danger',
  // Bumps on every open so ConfirmModal re-focuses when a dialog replaces another.
  openId: 0,
  // Prompt mode only: the controlled text-input value + placeholder.
  inputValue: '',
  placeholder: '',
  resolve: null,

  setInputValue: value => set({ inputValue: value }),

  open: options =>
    new Promise(resolve => {
      const mode = options.mode || 'confirm';
      // A dialog opened while another is still pending used to overwrite its
      // resolver, leaving the first caller awaiting forever. Dismiss it instead.
      const { resolve: pending, mode: pendingMode } = get();
      if (pending) pending(dismissResultFor(pendingMode));
      set({
        isOpen: true,
        title: options.title || 'Are you sure?',
        message: options.message || '',
        confirmLabel: options.confirmLabel || (mode === 'alert' ? 'OK' : 'Confirm'),
        cancelLabel: options.cancelLabel || 'Cancel',
        discardLabel: options.discardLabel || 'Discard',
        discardTone: options.discardTone === 'neutral' ? 'neutral' : 'danger',
        variant: resolveVariant(options),
        initialFocus: options.initialFocus === 'cancel' ? 'cancel' : 'confirm',
        openId: get().openId + 1,
        mode,
        inputValue: mode === 'prompt' ? options.defaultValue || '' : '',
        placeholder: options.placeholder || '',
        resolve,
      });
    }),

  // Resolves the caller's awaited promise. confirm/alert → boolean; prompt →
  // the entered string on confirm, or null on cancel/dismiss; choice →
  // 'save' | 'discard' | 'keep'.
  close: result => {
    const { resolve } = useConfirmStore.getState();
    set({ isOpen: false, resolve: null, inputValue: '', placeholder: '' });
    if (resolve) resolve(result);
  },
}));

/**
 * Hook that returns an async confirm function.
 *
 *   const confirm = useConfirm();
 *   if (await confirm({ title: 'Delete?', message: '...' })) { ... }
 */
export function useConfirm() {
  const open = useConfirmStore(s => s.open);
  return open;
}

/**
 * Hook that returns an async three-way choice function. Additive: `useConfirm`
 * is untouched and still resolves a boolean.
 *
 *   const choose = useChoice();
 *   const answer = await choose({ title: 'Unsaved changes', ... });  // 'save' | 'discard' | 'keep'
 *
 * Options: title, message, variant, initialFocus, plus the three labels
 * `saveLabel` (default 'Save'), `discardLabel` ('Discard') and `keepLabel`
 * ('Keep editing'). Escape, the backdrop and the keep button all resolve 'keep'.
 * `discardTone: 'neutral'` draws the discard action without the red styling, for a label
 * that does not throw anything away.
 *
 * TWO FOOTGUNS (proven by PoC in the OV-STACK review): a dismissed choice resolves the TRUTHY
 * string 'keep', so never test the result for truthiness (`if (await choose(...))` treats Cancel
 * as a confirmation): compare to 'save' / 'discard' / 'keep'. And `initialFocus` is NOT defaulted
 * to the safe button here: without `initialFocus: 'cancel'` the focused button is Save, so Enter
 * on open saves.
 */
export function useChoice() {
  const open = useConfirmStore(s => s.open);
  return useCallback(
    ({ saveLabel, keepLabel, ...options }) =>
      open({
        ...options,
        mode: 'choice',
        confirmLabel: saveLabel || 'Save',
        cancelLabel: keepLabel || 'Keep editing',
        // A choice is a navigation decision, not a deletion; the Discard button
        // carries its own destructive styling. Callers can still override.
        variant: options.variant || (options.danger ? 'danger' : 'primary'),
      }),
    [open]
  );
}

export default useConfirmStore;
